import { formatDateTime, formatMoney } from '@persona/shared';
import { Alert, Input, Skeleton, StatusPill } from '@persona/ui';
import { useEffect, useState } from 'react';
import { adminApi, ApiError, type AdminPaymentRow } from '../api';
import { EmptyRow, Th } from '../components/Shell';

const STATUS: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' | 'info' }> = {
  succeeded: { label: 'Оплачен', tone: 'success' },
  pending: { label: 'В обработке', tone: 'warning' },
  waiting_for_capture: { label: 'В обработке', tone: 'warning' },
  failed: { label: 'Не прошёл', tone: 'danger' },
  canceled: { label: 'Отменён', tone: 'neutral' },
  refunded: { label: 'Возвращён', tone: 'info' },
};

const KIND: Record<string, string> = {
  initial: 'Первая оплата',
  recurring: 'Автосписание',
  manual_renew: 'Продление вручную',
  card_binding: 'Привязка карты',
};

/** Все платежи, проведённые через кабинет. */
export function PaymentsPage({ onOpenClient }: { onOpenClient: (id: string) => void }) {
  const [rows, setRows] = useState<AdminPaymentRow[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refunding, setRefunding] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  /**
   * Возврат денег.
   *
   * Причину спрашиваем обязательно: она попадает в журнал, и без неё через
   * месяц невозможно понять, почему деньги вернули. Подтверждение — потому
   * что операция необратимая.
   */
  async function refund(row: AdminPaymentRow): Promise<void> {
    const remaining = row.amountMinor;
    const reason = window.prompt(
      `Возврат ${formatMoney(remaining)} клиенту ${row.client.name}.
Укажите причину — она сохранится в журнале:`,
    );
    if (!reason?.trim()) return;

    setRefunding(row.id);
    setError(null);
    setNotice(null);
    try {
      const result = await adminApi.refund(row.id, reason.trim());
      setNotice(
        result.remainingMinor > 0
          ? `Возвращено ${formatMoney(result.refundedMinor)}, осталось ${formatMoney(result.remainingMinor)}`
          : `Возвращено полностью: ${formatMoney(result.refundedMinor)}`,
      );
      setReload((value) => value + 1);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось выполнить возврат');
    } finally {
      setRefunding(null);
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setRows(null);
      adminApi
        .payments(search)
        .then((result) => setRows(result.items))
        .catch((cause: unknown) => {
          setError(cause instanceof ApiError ? cause.message : 'Не удалось загрузить платежи');
          setRows([]);
        });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, reload]);

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold tracking-wide uppercase">Платежи</h1>
      <p className="mt-2 text-sm text-secondary">
        Оплаты через кабинет. Покупки, оформленные на ресепшене, видны в карточке клиента — они приходят из 1С.
      </p>

      {error && <Alert tone="danger" className="mt-6">{error}</Alert>}
      {notice && <Alert tone="success" className="mt-6">{notice}</Alert>}

      <div className="mt-6 max-w-md">
        <Input
          type="search"
          placeholder="Поиск по клиенту"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-strong)]">
              <Th>Клиент</Th>
              <Th>Тип</Th>
              <Th>Сумма</Th>
              <Th>Статус</Th>
              <Th>Карта</Th>
              <Th>Дата</Th>
              <Th>В 1С</Th>
              <Th>Действия</Th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              Array.from({ length: 4 }, (_, index) => (
                <tr key={index} className="border-b border-[var(--border-subtle)]">
                  <td className="px-3 py-4" colSpan={8}>
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <EmptyRow colSpan={8}>Платежей пока нет: оплата через ЮKassa ещё не подключена.</EmptyRow>
            ) : (
              rows.map((row) => {
                const status = STATUS[row.status] ?? { label: row.status, tone: 'neutral' as const };
                return (
                  <tr key={row.id} className="border-b border-[var(--border-subtle)] align-top">
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => onOpenClient(row.client.id)}
                        className="text-left font-medium text-[var(--accent)] hover:underline"
                      >
                        {row.client.name}
                      </button>
                      <div className="text-xs text-muted">{row.client.phone}</div>
                      {/* Демонстрационную запись нельзя спутать с настоящей продажей */}
                      {row.isDemo && (
                        <div className="mt-1 inline-block rounded bg-[var(--surface-muted)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted">
                          демо
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">{KIND[row.kind] ?? row.kind}</td>
                    <td className="px-3 py-3 font-medium whitespace-nowrap">{formatMoney(row.amountMinor)}</td>
                    <td className="px-3 py-3">
                      <StatusPill tone={status.tone}>{status.label}</StatusPill>
                      {row.errorMessage && <div className="mt-1 text-xs text-danger-500">{row.errorMessage}</div>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">{row.cardMask ?? <span className="text-muted">—</span>}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-muted">{formatDateTime(row.createdAt)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {row.onecPostedAt ? (
                        <span className="text-xs">зафиксирован</span>
                      ) : row.isDemo ? (
                        // Для демонстрации это норма, а не расхождение.
                        <span className="text-xs text-muted">не требуется</span>
                      ) : row.status === 'succeeded' ? (
                        // Деньги списаны, а продажи в учёте нет — это надо чинить вручную.
                        <span className="text-xs text-danger-500">не зафиксирован</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {row.status === 'succeeded' && !row.isDemo ? (
                        <button
                          type="button"
                          disabled={refunding === row.id}
                          onClick={() => void refund(row)}
                          className="text-xs font-medium uppercase tracking-wide text-[var(--accent)] hover:underline disabled:opacity-50"
                        >
                          {refunding === row.id ? 'Возврат…' : 'Вернуть'}
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
